import React, {ReactElement, createContext, useCallback, useContext, useRef} from 'react';
import {doNothing} from '~/library/toolbox/src';
import {joinCallbacks} from '~/library/ui-kit/src/Utilities/joinCallbacks';
import {useNonReactiveCallback, useShallowMemo} from '../../Hooks';
import {useRootElement} from '../../Utilities';
import PortalTo from '../../Utilities/PortalTo';
import {useDialogRegistration} from './DialogsRegistry';
import {MountControl, MountControlChild, isMountable, useMountControl} from './MountControl';

type CloseCallback = ({exitDone}: {exitDone: Promise<void>}) => void;

export type DialogControllerProps = {
    onClose?: CloseCallback;
    onExitEnd?: () => void;
    portalTo?: HTMLElement;
    dialogId?: string;
} & (ControlledByProp | ControlledByChildren);

type ControlledByProp = {
    isOpen: boolean;
    children: ReactElement;
};

type ControlledByChildren = {
    isOpen?: undefined;
    children: MountControlChild;
};

/**
 * Allows to mount and unmount Dialog.Window with animations.
 * It also enforces that react components of modals aren't mounted in vDOM when they aren't open.
 *
 * **IMPORTANT** to keep in mind that when modal is closing there's no longer a chance to update it's props.
 */
export function DialogController({
    isOpen: externalIsOpen,
    onClose: externalOnClose,
    onExitEnd: externalOnExitEnd,
    children,
    portalTo,
    dialogId,
}: DialogControllerProps) {
    const rootElement = useRootElement();

    const exitEnd = useRef(Promise.withResolvers<void>());

    const onClose = useNonReactiveCallback(() => {
        const {promise} = exitEnd.current;
        externalOnClose?.({exitDone: promise});
        return promise;
    });

    if (externalIsOpen !== undefined) {
        children = externalIsOpen && children;
    }
    const isOpen = isMountable(children);
    const {isActive, notifyExited} = useDialogRegistration(isOpen, onClose, dialogId);

    if (!isActive) {
        children = false;
    }

    const internalOnExitEnd = useCallback(() => {
        exitEnd.current.resolve();
        exitEnd.current = Promise.withResolvers();
        notifyExited();
    }, [notifyExited]);

    const onExitEnd = joinCallbacks(internalOnExitEnd, externalOnExitEnd);

    return (
        <PortalTo element={portalTo || rootElement}>
            <DialogControlsContext.Provider value={useShallowMemo({onExitEnd, onClose})}>
                <MountControl>{children}</MountControl>
            </DialogControlsContext.Provider>
        </PortalTo>
    );
}

type DialogControls = {
    onClose: () => void;
    onExitEnd: () => void;
};

export const DialogControlsContext = createContext<DialogControls>({onClose: doNothing, onExitEnd: doNothing});

export function useDialogControls() {
    return useContext(DialogControlsContext);
}

export function useDialogCurrentState() {
    const {isMounted} = useMountControl();
    return {isOpened: isMounted};
}
